import { inject, Injectable } from '@angular/core';
import {
  AssetExpressionsParams,
  EmGetGroupedAssetsRawGQL,
  EmGetVulnerabilitiesCountGQL,
  FindingsObjExpressionsParams,
  GetAssetsCountGQL,
  GetFindingsCountGQL,
  GroupedAssetExpressionsParams,
  IcpPluginExpressionsParams,
} from '@uigy/generated/graphql';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MainKpiData } from '@uigy/modules/overview/kpis/kpi.interfaces';
import { StatusIconType } from '@uigy/shared/components/icons/interfaces';
import Utils from '@uigy/utils';
import { MainKpiExpressionParamsTypes } from '@uigy/modules/overview/kpis/kpi.types';
import { UiGridFilterModel } from '@uigy/shared/components/ui-grid/models/ui-grid';

@Injectable({
  providedIn: 'root'
})
export class MainKpiService {
  private getAssetsCountGQL = inject(GetAssetsCountGQL);
  private getFindingsCountGQL = inject(GetFindingsCountGQL);
  private emGetGroupedAssetsRawGQL = inject(EmGetGroupedAssetsRawGQL);
  private emGetVulnerabilitiesCountGQL = inject(EmGetVulnerabilitiesCountGQL);

  constructor() {
  }

  public fetchCombinedKpiCount<T = MainKpiExpressionParamsTypes>(filters: UiGridFilterModel<any>[], query: (filter: T) => Observable<number>): Observable<MainKpiData> {
    const [filter, riskFilter] = filters;
    return combineLatest([query(filter as T), query(riskFilter as T)]).pipe(
      map(([count, riskCount]) => this.mapCountsToKpiData(count, riskCount))
    );
  }

  public getAssetsCount = (filter: AssetExpressionsParams): Observable<number> => {
    return this.getAssetsCountGQL.fetch({filter}).pipe(
      map(({data}) => data.assetsForGrid.totalCount),
    );
  };

  public getGroupedAssetsRawGQL = (filter: GroupedAssetExpressionsParams): Observable<number> => {
    return this.emGetGroupedAssetsRawGQL.fetch({filter}).pipe(
      map(({data}) => Utils.stripConnection(data.groupedAssetsRaw)?.[0]?.count ?? 0)
    );
  };

  public getFindingsCount = (filter: FindingsObjExpressionsParams): Observable<number> => {
    return this.getFindingsCountGQL.fetch({filter}).pipe(
      map(({data}) => data.findings.totalCount),
    );
  };

  public getVulnerabilitiesCount = (filter: IcpPluginExpressionsParams): Observable<number> => {
    return this.emGetVulnerabilitiesCountGQL.fetch({filter}).pipe(
      map(({data}) => {
        const nodes = Utils.stripConnection(data.iemPluginsRaw) || [];
        return nodes.reduce((total, node) => total + (node.count ?? 0), 0);
      }),
    );
  };

  private mapCountsToKpiData(count: number, riskCount: number): MainKpiData {
    return {
      count,
      riskCount,
      statusIcon: riskCount > 0 ? StatusIconType.negative : StatusIconType.positive,
    };
  }
}
